import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import { apiRequest } from "../../services/api";
const input = "mt-1 block w-full rounded-lg border bg-white p-2 text-sm",
  button = "rounded-lg border px-3 py-2 text-sm disabled:opacity-40";
const header = "date,reference,description,debit,credit,balance";
function download(bytes, name) {
  const url = URL.createObjectURL(
      new Blob([bytes], { type: "application/octet-stream" }),
    ),
    a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export default function BankStatements({ companyId }) {
  const { accessToken, user } = useAuth(),
    canWrite = hasPermission(user, "accounting.vouchers_post"),
    canReverse = hasPermission(user, "accounting.vouchers_reverse");
  const [data, setData] = useState(null),
    [rows, setRows] = useState(null),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [file, setFile] = useState(null),
    [csv, setCsv] = useState(""),
    [preview, setPreview] = useState(null),
    [checks, setChecks] = useState({}),
    [selectedRow, setSelectedRow] = useState(null),
    [candidates, setCandidates] = useState([]),
    [matchPreview, setMatchPreview] = useState(null),
    [matchConfirmed, setMatchConfirmed] = useState(false),
    [correction, setCorrection] = useState(null),
    [report, setReport] = useState(null);
  const generation = useRef(0),
    working = useRef(false),
    keys = useRef({}),
    fileGeneration = useRef({ source: 0, csv: 0 });
  const url = `/accounting/${companyId}/bank-statements`;
  useEffect(() => {
    generation.current++;
    working.current = false;
    keys.current = {};
    fileGeneration.current.source++;
    fileGeneration.current.csv++;
    setData(null);
    setRows(null);
    setFile(null);
    setCsv("");
    setPreview(null);
    setMatchPreview(null);
    setReport(null);
    setBusy(false);
    setError("");
    setNotice("");
    return () => {
      generation.current++;
      fileGeneration.current.source++;
      fileGeneration.current.csv++;
    };
  }, [companyId, accessToken]);
  function requestKey(kind, body) {
    const signature = JSON.stringify(body);
    if (keys.current[kind]?.signature !== signature) {
      if (!window.crypto?.randomUUID)
        throw Error("HTTPS or localhost is required for secure request keys");
      keys.current[kind] = { signature, key: window.crypto.randomUUID() };
    }
    return keys.current[kind].key;
  }
  async function run(work, done) {
    if (working.current) return;
    working.current = true;
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await work();
      if (g === generation.current) done(r);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) {
        working.current = false;
        setBusy(false);
      }
    }
  }
  const workspace = async () => {
    const [w, s] = await Promise.all([
      apiRequest(`/accounting/${companyId}/workspace`, { accessToken }),
      apiRequest(url, { accessToken }),
    ]);
    return { ...s, years: w.years };
  };
  function clearPreview() {
    setPreview(null);
    setChecks({});
  }
  async function readFile(event, kind) {
    const f = event.target.files?.[0],
      n = ++fileGeneration.current[kind];
    clearPreview();
    if (kind === "source") setFile(null);
    else setCsv("");
    if (!f) return;
    try {
      if (!f.size || f.size > 1048576)
        throw Error("Choose a file from 1 byte to 1 MiB");
      const buffer = await f.arrayBuffer();
      if (n !== fileGeneration.current[kind]) return;
      if (kind === "source") {
        const bytes = new Uint8Array(buffer),
          parts = [];
        for (let i = 0; i < bytes.length; i += 8192)
          parts.push(String.fromCharCode(...bytes.subarray(i, i + 8192)));
        setFile({ sourceName: f.name, sourceBase64: btoa(parts.join("")) });
      } else
        setCsv(
          new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(
            buffer,
          ),
        );
    } catch (e) {
      if (n === fileGeneration.current[kind]) setError(e.message);
    }
  }
  function importPreview(e) {
    e.preventDefault();
    if (!file || !csv || busy) return;
    const values = Object.fromEntries(new FormData(e.currentTarget)),
      body = {
        bankLedgerId: values.bankLedgerId,
        currency: "INR",
        periodStart: values.periodStart,
        periodEnd: values.periodEnd,
        openingBalance: values.openingBalance,
        closingBalance: values.closingBalance,
        reviewNote: values.reviewNote,
        ...(values.noTransactionsConfirmed === "on"
          ? { noTransactionsConfirmed: true }
          : {}),
        ...file,
        csvText: csv,
      };
    clearPreview();
    run(
      () => apiRequest(`${url}/preview`, { accessToken, method: "POST", body }),
      (r) => setPreview({ ...r, command: body }),
    );
  }
  function saveImport() {
    if (
      !canWrite ||
      !preview ||
      busy ||
      ![
        "accountOwnershipReviewed",
        "conversionReviewed",
        "balanceReviewed",
      ].every((k) => checks[k] === true)
    )
      return;
    const body = {
      ...preview.command,
      previewHash: preview.previewHash,
      ...checks,
    };
    body.requestKey = requestKey("import", body);
    run(
      async () => {
        const saved = await apiRequest(url, {
          accessToken,
          method: "POST",
          body,
        });
        return {
          data: await workspace(),
          rows: await apiRequest(`${url}/${saved.id}/rows`, { accessToken }),
        };
      },
      (r) => {
        setData(r.data);
        setRows(r.rows);
        clearPreview();
        setReport(null);
        setNotice(
          "Private source and statement rows retained. No financial voucher or bank transfer was created.",
        );
      },
    );
  }
  function showRows(id, offset = 0) {
    setSelectedRow(null);
    setMatchPreview(null);
    setCorrection(null);
    run(
      () => apiRequest(`${url}/${id}/rows?offset=${offset}`, { accessToken }),
      setRows,
    );
  }
  function find(row) {
    setSelectedRow(row);
    setMatchPreview(null);
    setMatchConfirmed(false);
    setCandidates([]);
    run(
      () => apiRequest(`${url}/rows/${row.id}/candidates`, { accessToken }),
      (r) => setCandidates(r.rows),
    );
  }
  function planMatch(e) {
    e.preventDefault();
    if (!selectedRow) return;
    const body = Object.fromEntries(new FormData(e.currentTarget));
    setMatchPreview(null);
    setMatchConfirmed(false);
    run(
      () =>
        apiRequest(`${url}/rows/${selectedRow.id}/preview`, {
          accessToken,
          method: "POST",
          body,
        }),
      (r) => setMatchPreview({ ...r, command: body }),
    );
  }
  function saveMatch() {
    if (!canWrite || !matchPreview || !matchConfirmed || busy) return;
    const body = {
      ...matchPreview.command,
      previewHash: matchPreview.previewHash,
      referenceAndTimingReviewed: true,
    };
    body.requestKey = requestKey("bank-match", body);
    run(
      async () => {
        await apiRequest(
          `${url}/rows/${matchPreview.payload.statementRowId}/match`,
          { accessToken, method: "POST", body },
        );
        return apiRequest(
          `${url}/${rows.statement.id}/rows?offset=${rows.offset}`,
          { accessToken },
        );
      },
      (r) => {
        setRows(r);
        setSelectedRow(null);
        setMatchPreview(null);
        setReport(null);
        setNotice(
          "Reviewed bank-row match recorded. Source authenticity and provider verification remain separate.",
        );
      },
    );
  }
  function correct(e) {
    e.preventDefault();
    if (!canReverse || !correction || busy) return;
    const f = Object.fromEntries(new FormData(e.currentTarget));
    if (f.confirmed !== "on" || f.reason.trim().length < 20) return;
    const body = { reason: f.reason.trim(), correctionConfirmed: true };
    body.requestKey = requestKey(correction.kind + "-" + correction.id, body);
    const path =
      correction.kind === "release"
        ? `${url}/matches/${correction.id}/release`
        : `${url}/${correction.id}/void`;
    run(
      async () => {
        await apiRequest(path, { accessToken, method: "POST", body });
        return {
          data: await workspace(),
          rows: rows
            ? await apiRequest(
                `${url}/${rows.statement.id}/rows?offset=${rows.offset}`,
                { accessToken },
              )
            : null,
        };
      },
      (r) => {
        setData(r.data);
        setRows(r.rows);
        setCorrection(null);
        setSelectedRow(null);
        setMatchPreview(null);
        setReport(null);
        setNotice(
          "Reviewed correction retained in history. No book balance or bank transfer was changed.",
        );
      },
    );
  }
  function source(id) {
    run(
      () => apiRequest(`${url}/${id}/source`, { accessToken }),
      (r) => {
        download(
          Uint8Array.from(atob(r.sourceBase64), (c) => c.charCodeAt(0)),
          r.sourceName,
        );
        setNotice(
          "Exact retained source downloaded privately. Only open files you trust.",
        );
      },
    );
  }
  function review(e) {
    e.preventDefault();
    const q = new URLSearchParams(
      Object.fromEntries(new FormData(e.currentTarget)),
    );
    setReport(null);
    run(
      () => apiRequest(`${url}/reconciliation?${q}`, { accessToken }),
      setReport,
    );
  }
  const bankOptions = () => (
    <>
      <option value="">Select bank ledger</option>
      {data.banks.map((b) => (
        <option key={b.id} value={b.id}>
          {b.code} · {b.name}
        </option>
      ))}
    </>
  );
  return (
    <section className="space-y-5 rounded-2xl border bg-white p-5">
      <h2 className="text-xl font-semibold">
        Bank statements & reviewed reconciliation
      </h2>
      <p className="text-sm text-slate-600">
        Private source retention and explicit one-row/one-book-line matching.
        Native Cashfree, terminal and bank layouts are not guessed. Canonical
        import and manual review do not authenticate a file or verify the
        provider.
      </p>
      <button
        className={button}
        disabled={busy || !companyId}
        onClick={() => run(workspace, setData)}
      >
        Load bank statement workspace
      </button>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-800">
          {notice}
        </p>
      )}
      {data && (
        <>
          {canWrite && (
            <details className="rounded-xl border p-4">
              <summary className="cursor-pointer font-semibold">
                Import a bank statement
              </summary>
              <p className="my-3 text-sm">
                Retain the original source separately from any converted CSV.
                The canonical CSV must contain every row in date order, explicit
                zero amounts and each running balance. Maximum 1000 rows and 1
                MiB per file. Wrong imports must be reviewed and voided; history
                is never deleted.
              </p>
              <code className="block overflow-auto text-xs">{header}</code>
              <button
                className={button}
                type="button"
                disabled={busy}
                onClick={() =>
                  download(header + "\r\n", "canonical-bank-header.csv")
                }
              >
                Download header-only CSV template
              </button>
              <form
                className="mt-3 grid gap-3 md:grid-cols-2"
                onSubmit={importPreview}
                onChange={clearPreview}
              >
                <fieldset disabled={busy} className="contents">
                  <label>
                    Statement bank ledger
                    <select required name="bankLedgerId" className={input}>
                      {bankOptions()}
                    </select>
                  </label>
                  <label>
                    Period starts
                    <input
                      required
                      type="date"
                      name="periodStart"
                      className={input}
                    />
                  </label>
                  <label>
                    Period ends
                    <input
                      required
                      type="date"
                      name="periodEnd"
                      className={input}
                    />
                  </label>
                  <label>
                    Opening balance (INR)
                    <input
                      required
                      name="openingBalance"
                      className={input}
                      pattern="-?[0-9]+(\.[0-9]{1,2})?"
                    />
                  </label>
                  <label>
                    Closing balance (INR)
                    <input
                      required
                      name="closingBalance"
                      className={input}
                      pattern="-?[0-9]+(\.[0-9]{1,2})?"
                    />
                  </label>
                  <label>
                    Original source file (private)
                    <input
                      required
                      type="file"
                      className={input}
                      onChange={(e) => readFile(e, "source")}
                    />
                  </label>
                  <label>
                    Canonical UTF-8 CSV
                    <input
                      required
                      type="file"
                      accept=".csv,text/csv"
                      className={input}
                      onChange={(e) => readFile(e, "csv")}
                    />
                  </label>
                  <label>
                    Account and conversion review note
                    <textarea
                      required
                      name="reviewNote"
                      minLength={20}
                      maxLength={1000}
                      className={input}
                    />
                  </label>
                  <label className="text-sm">
                    <input type="checkbox" name="noTransactionsConfirmed" />{" "}
                    This source period has no transactions; the CSV contains
                    only the header and opening equals closing.
                  </label>
                  <button className={button} disabled={!file || !csv}>
                    Preview statement import
                  </button>
                </fieldset>
              </form>
              {preview && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm">{preview.warning}</p>
                  <p className="text-sm">
                    {preview.payload.rows.length} rows · Source SHA-256{" "}
                    {preview.payload.sourceSha}
                  </p>
                  <div className="max-h-80 overflow-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr>
                          <th>Date/reference</th>
                          <th>Description</th>
                          <th>Debit</th>
                          <th>Credit</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.payload.rows.map((r) => (
                          <tr key={r.rowNumber} className="border-t">
                            <td>
                              {r.date}
                              <br />
                              {r.reference || "No reference"}
                            </td>
                            <td>{r.description}</td>
                            <td>{r.debit}</td>
                            <td>{r.credit}</td>
                            <td>{r.balance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {[
                    [
                      "accountOwnershipReviewed",
                      "I checked that the retained source belongs to this company and bank account.",
                    ],
                    [
                      "conversionReviewed",
                      "I compared every canonical row against the original, including references, dates and amounts.",
                    ],
                    [
                      "balanceReviewed",
                      "I reviewed the opening, closing and every running balance.",
                    ],
                  ].map(([key, label]) => (
                    <label key={key} className="block text-sm">
                      <input
                        type="checkbox"
                        checked={!!checks[key]}
                        onChange={(e) =>
                          setChecks({ ...checks, [key]: e.target.checked })
                        }
                      />{" "}
                      {label}
                    </label>
                  ))}
                  <button
                    type="button"
                    className={button}
                    disabled={
                      busy || Object.values(checks).filter(Boolean).length !== 3
                    }
                    onClick={saveImport}
                  >
                    Import reviewed private statement
                  </button>
                </div>
              )}
            </details>
          )}
          <section className="space-y-3">
            <h3 className="font-semibold">Retained statement history</h3>
            {data.statements.map((s) => (
              <article
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm"
              >
                <div>
                  <strong>
                    {data.banks.find((b) => b.id === s.bankLedgerId)?.name ||
                      s.bankLedgerId}
                  </strong>
                  <p>
                    {s.periodStart.slice(0, 10)} to {s.periodEnd.slice(0, 10)} ·{" "}
                    {s.rowCount} rows ·{" "}
                    {s.voidedId ? "Voided, history retained" : "Active"}
                  </p>
                  <p>
                    {s.sourceName} · closing ₹{s.closingBalance}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={busy}
                    className={button}
                    onClick={() => showRows(s.id)}
                  >
                    View rows
                  </button>
                  <button
                    disabled={busy}
                    className={button}
                    onClick={() => source(s.id)}
                  >
                    Download retained source
                  </button>
                  {canReverse && !s.voidedId && (
                    <button
                      disabled={busy}
                      className={button}
                      onClick={() => setCorrection({ kind: "void", id: s.id })}
                    >
                      Void incorrect import
                    </button>
                  )}
                </div>
              </article>
            ))}
            {!data.statements.length && (
              <p className="text-sm">No statement sources retained.</p>
            )}
            <p className="text-xs text-slate-500">
              Latest {data.limit} imports shown; loaded row pages contain 100
              rows.
            </p>
          </section>
          {rows && (
            <section className="space-y-3 rounded-xl border p-4">
              <h3 className="font-semibold">
                Statement rows · {rows.statement.sourceName}
              </h3>
              {rows.voidEvent && (
                <p className="text-sm">
                  Voided: {rows.voidEvent.reason} · {rows.voidEvent.createdAt}
                </p>
              )}
              <div className="overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>Date/reference</th>
                      <th>Description</th>
                      <th>Bank debit</th>
                      <th>Bank credit</th>
                      <th>Balance</th>
                      <th>Reviewed match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.rows.map((r) => {
                      const match = r.matches.find((m) => !m.releasedId);
                      return (
                        <tr key={r.id} className="border-t">
                          <td>
                            {r.date.slice(0, 10)}
                            <br />
                            {r.reference || "No reference"}
                          </td>
                          <td>{r.description}</td>
                          <td>{r.debit}</td>
                          <td>{r.credit}</td>
                          <td>{r.balance}</td>
                          <td>
                            {match ? (
                              <>
                                <span>{match.bankLine.voucher.number}</span>
                                {canReverse && (
                                  <button
                                    disabled={busy}
                                    className={button}
                                    onClick={() =>
                                      setCorrection({
                                        kind: "release",
                                        id: match.id,
                                      })
                                    }
                                  >
                                    Release bank match
                                  </button>
                                )}
                              </>
                            ) : !r.voidedId ? (
                              <button
                                disabled={busy}
                                className={button}
                                onClick={() => find(r)}
                              >
                                Find exact book lines
                              </button>
                            ) : (
                              <span>Voided</span>
                            )}
                            {r.matches
                              .filter((m) => m.releasedId)
                              .map((m) => (
                                <p className="text-xs" key={m.id}>
                                  Released:{" "}
                                  {rows.releases?.find(
                                    (x) => x.id === m.releasedId,
                                  )?.reason || m.releasedId}
                                </p>
                              ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between">
                <button
                  className={button}
                  disabled={busy || rows.offset === 0}
                  onClick={() =>
                    showRows(rows.statement.id, Math.max(0, rows.offset - 100))
                  }
                >
                  Previous rows
                </button>
                <span className="text-sm">{rows.total} rows</span>
                <button
                  className={button}
                  disabled={busy || rows.offset + 100 >= rows.total}
                  onClick={() => showRows(rows.statement.id, rows.offset + 100)}
                >
                  Next rows
                </button>
              </div>
            </section>
          )}
          {selectedRow && (
            <form
              onSubmit={planMatch}
              onChange={() => {
                setMatchPreview(null);
                setMatchConfirmed(false);
              }}
              className="space-y-3 rounded-xl border p-4"
            >
              <p className="text-sm">
                Selected bank row: {selectedRow.reference || "No reference"} ·{" "}
                {selectedRow.date.slice(0, 10)}. Exact account/sign/amount
                candidates only; same India fiscal year. Review identity and
                timing manually.
              </p>
              <label>
                Book bank line
                <select
                  required
                  name="bankLineId"
                  className={input}
                  defaultValue=""
                >
                  <option value="">Select a reviewed book entry</option>
                  {candidates.map((l) => (
                    <option key={l.bankLineId} value={l.bankLineId}>
                      {l.number} · {l.date} · Dr {l.debit} / Cr {l.credit} ·{" "}
                      {l.narration}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Reference and timing review
                <textarea
                  required
                  name="reviewNote"
                  minLength={20}
                  maxLength={1000}
                  className={input}
                />
              </label>
              <button disabled={busy || !candidates.length} className={button}>
                Preview bank match
              </button>
              {!candidates.length && (
                <p className="text-sm">
                  No exact eligible book line found. No journal is created
                  automatically.
                </p>
              )}
            </form>
          )}
          {matchPreview && (
            <div className="space-y-3 rounded-xl border p-4">
              <p className="text-sm">{matchPreview.warning}</p>
              <p className="text-sm">
                {matchPreview.voucherNumber} · book{" "}
                {matchPreview.payload.bookDate} · bank{" "}
                {matchPreview.payload.bankDate}
              </p>
              {canWrite && (
                <>
                  <label className="block text-sm">
                    <input
                      type="checkbox"
                      checked={matchConfirmed}
                      onChange={(e) => setMatchConfirmed(e.target.checked)}
                    />{" "}
                    I reviewed the original reference, account, amount and any
                    date/timing difference.
                  </label>
                  <button
                    className={button}
                    disabled={busy || !matchConfirmed}
                    onClick={saveMatch}
                  >
                    Record reviewed bank match
                  </button>
                </>
              )}
            </div>
          )}
          {correction && canReverse && (
            <form
              onSubmit={correct}
              className="space-y-3 rounded-xl border border-red-200 p-4"
            >
              <h3 className="font-semibold">
                {correction.kind === "release"
                  ? "Release bank match"
                  : "Void imported statement"}
              </h3>
              <label>
                Correction reason
                <textarea
                  required
                  name="reason"
                  minLength={20}
                  maxLength={1000}
                  className={input}
                />
              </label>
              <label className="block text-sm">
                <input required type="checkbox" name="confirmed" /> I reviewed
                this correction; original history must remain.
              </label>
              <button className={button} disabled={busy}>
                Record reviewed correction
              </button>
              <button
                type="button"
                className={button}
                disabled={busy}
                onClick={() => setCorrection(null)}
              >
                Cancel correction
              </button>
            </form>
          )}
          <section className="rounded-xl border p-4">
            <h3 className="font-semibold">Dated bank reconciliation review</h3>
            <form onSubmit={review} className="mt-3 grid gap-3 md:grid-cols-4">
              <label>
                Review bank ledger
                <select required name="bankLedgerId" className={input}>
                  {bankOptions()}
                </select>
              </label>
              <label>
                Review financial year
                <select
                  required
                  name="yearId"
                  className={input}
                  defaultValue=""
                >
                  <option value="">Select year</option>
                  {data.years.map((y) => (
                    <option key={y.id} value={y.id}>
                      {y.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Review as of
                <input type="date" name="asOf" required className={input} />
              </label>
              <button disabled={busy} className={button}>
                Load bank reconciliation review
              </button>
            </form>
            {report && (
              <div className="mt-4 space-y-2 text-sm">
                <p className="rounded-lg bg-amber-50 p-3">{report.warning}</p>
                <p>
                  Book balance ₹{report.bookBalance} · Statement closing{" "}
                  {report.statementClosing === null
                    ? "unavailable — incomplete coverage"
                    : `₹${report.statementClosing}`}
                </p>
                <p>
                  Continuous year-to-date coverage:{" "}
                  {report.fullPeriodCoverage ? "Yes" : "No"} · Opening balance
                  agreement:{" "}
                  {report.yearOpeningAgrees ? "Yes" : "No / unavailable"}
                </p>
                <p>
                  Unmatched bank rows: {report.unmatchedBankRows} (₹
                  {report.unmatchedBankNet}) · Unmatched book lines:{" "}
                  {report.unmatchedBookLines} (₹{report.unmatchedBookNet})
                </p>
                <p>
                  Explained bank balance ₹{report.explainedBankBalance} ·
                  Unexplained difference{" "}
                  {report.unexplainedDifference === null
                    ? "unavailable"
                    : `₹${report.unexplainedDifference}`}
                </p>
                <p className="font-semibold">
                  Manual review required. No automatic reconciled, year-close or
                  provider-verified status.
                </p>
              </div>
            )}
          </section>
        </>
      )}
    </section>
  );
}
